<template>
	<div class="chat-selector">
		<AppDropdownMenu
			ref="chat-dropdown"
			class="chat-dropdown-menu"
			:options="chatOptions"
			@selected="handleChatSelect"
			@showDropdown="handleShowDropdown"
			@hideDropdown="handleHideDropdown"
			:rightAlign="false"
			:dropdownAutoClose="true"
			:currentValue="currentChat"
			:minWidth="0"
		>
			<div class="chat-selector-trigger" @click="toggleDropdown">
				<div class="chat-title">{{ currentChatTitle }}</div>
				<InlineSvg class="dropdown-icon" :class="{ rotated: isDropdownOpen }" src="/images/dropdown-arrow.svg" />
			</div>
		</AppDropdownMenu>
	</div>
</template>

<script>
	export default {
		name: 'ChatSelector',
		data() {
			return {
				isDropdownOpen: false,
			}
		},
		props: {
			chats: {
				type: Array,
				default: () => [],
			},
			currentChatId: {
				type: [Number, String],
				default: null,
			},
		},
		computed: {
			chatOptions() {
				return this.chats.map(chat => ({
					id: chat.id,
					label: this.getChatDisplayTitle(chat),
					value: chat.id,
				}))
			},
			currentChat() {
				return this.chats.find(chat => chat.id === this.currentChatId) || this.chats[0]
			},
			currentChatTitle() {
				return this.currentChat ? this.getChatDisplayTitle(this.currentChat) : 'Select Chat'
			},
		},
		methods: {
			getChatDisplayTitle(chat) {
				if (!chat) return 'Select Chat'

				// If title is "New Chat" and has messages, use first 20 characters of first message
				if (chat.title === 'New Chat' && chat.messages && chat.messages.length > 0) {
					const firstMessage = chat.messages[0]
					const messageText = firstMessage.content || firstMessage.content_html || ''
					// Strip HTML tags if content_html is used
					const plainText = messageText.replace(/<[^>]*>/g, '').trim()
					if (plainText.length > 20) {
						// eslint-disable-next-line
						return plainText.substring(0, 20) + '...'
					}
					return plainText
				}

				return chat.title
			},
			handleChatSelect(option) {
				this.$emit('chat-selected', option.value)
			},
			handleShowDropdown() {
				this.isDropdownOpen = true

				// Track dropdown opened event
				this.$stanAnalytics('chat-v2-dropdown-opened', {
					meta: { user_id: this.$store.getters['Auth/user']?.user_id, username: this.$store.getters['Auth/user']?.username },
					props: { current_chat_id: this.currentChatId, platform: this.$commonHelper?.isMobileWidthOrLess() ? 'mobile' : 'desktop' },
				})
			},
			handleHideDropdown() {
				this.isDropdownOpen = false
			},
			toggleDropdown() {
				this.$refs['chat-dropdown'].toggle()
			},
		},
	}
</script>

<style lang="scss" scoped>
	.chat-selector {
		flex: 1;
		margin: 0 16px;
		display: flex;
		justify-content: center;
	}

	.chat-dropdown-menu {
		width: 100%;
		max-width: 300px;
	}

	.chat-selector-trigger {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 8px;
		padding: 8px 12px;
		border-radius: 8px;
		cursor: pointer;
		transition: all 0.2s ease;
		background: none;
		border: none;
		color: #111827;
		font-weight: 600;
		font-size: 16px;

		&:hover {
			background: #e5e7eb;
		}

		&:focus {
			outline: none;
			background: #e5e7eb;
		}
	}

	.chat-title {
		overflow: hidden;
		color: #131f60;
		text-align: center;
		text-overflow: ellipsis;
		white-space: nowrap;
		font-family: 'Plus Jakarta Sans';
		font-size: 16px;
		font-style: normal;
		font-weight: 600;
		line-height: 24px;
		letter-spacing: 0.16px;
		max-width: 200px;
	}

	.dropdown-icon {
		color: #6b7280;
		transition: transform 0.2s ease;
		flex-shrink: 0;

		&.rotated {
			transform: rotate(180deg);
		}
	}

	.chat-dropdown-menu::v-deep {
		.menu-content {
			min-width: 200px;
			max-width: 300px;
			transition: opacity 0.2s ease, transform 0.2s ease;
			opacity: 0;
			transform: translateY(-10px);
		}

		.menu-content.show {
			opacity: 1;
			transform: translateY(0);
		}

		.dropdown-item {
			padding: 12px 16px;
			font-size: 14px;
			font-weight: 500;
			color: #111827;
			border-radius: 8px;
			margin: 2px 0;

			&:hover {
				background: #f3f4f6;
			}

			&:active {
				background: #3b82f6;
				color: white;
			}
		}
	}

	@media (max-width: 768px) {
		.chat-selector {
			margin: 0 8px;
		}

		.chat-title {
			max-width: 160px;
			font-size: 14px;
		}

		.chat-dropdown-menu::v-deep {
			.menu-content {
				min-width: 100%;
			}
		}
	}
</style>
